import { useCallback, useMemo, useState } from 'react';

import { TIPO_PF, type Cliente } from '@/api/clientes';

import {
  clienteToForm,
  emptyForm,
  TOTAL_STEPS,
  validateStep,
  type ClienteFormData,
} from './clienteFormUtils';

const ALL_STEPS = new Set(Array.from({ length: TOTAL_STEPS }, (_, i) => i));

export function useClienteForm(cliente?: Cliente | null, initialStep = 0) {
  const isEditing = !!cliente;

  const [form, setForm] = useState<ClienteFormData>(
    cliente ? clienteToForm(cliente) : emptyForm,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [currentStep, setCurrentStep] = useState(isEditing ? initialStep : 0);
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(
    () => isEditing ? new Set(ALL_STEPS) : new Set([0]),
  );

  // ID do cliente salvo (para clientes recém-criados no step 0)
  const [savedClienteId, setSavedClienteId] = useState<string | null>(
    cliente?.id ?? null,
  );

  const isPF = form.tipo_pessoa === TIPO_PF;

  const set = useCallback((field: keyof ClienteFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const runValidation = useCallback((step: number): Record<string, string> => {
    const errs = validateStep(step, form, isPF);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      const firstField = Object.keys(errs)[0];
      document.getElementById(firstField)?.focus();
    }
    return errs;
  }, [form, isPF]);

  /** Valida o step atual sem avançar. Retorna true se válido. */
  const validateCurrentStep = useCallback((): boolean => {
    const errs = runValidation(currentStep);
    return Object.keys(errs).length === 0;
  }, [currentStep, runValidation]);

  const goNext = useCallback((): boolean => {
    if (!validateCurrentStep()) return false;
    const next = Math.min(currentStep + 1, TOTAL_STEPS - 1);
    setCurrentStep(next);
    setVisitedSteps((prev) => {
      if (prev.has(next)) return prev;
      return new Set([...prev, next]);
    });
    return true;
  }, [currentStep, validateCurrentStep]);

  /** Chamado após o POST de criação no step 0 — seta o ID, desbloqueia todos os steps e avança para step 1. */
  const handleCreated = useCallback((clienteId: string) => {
    setSavedClienteId(clienteId);
    setVisitedSteps(new Set(ALL_STEPS));
    setCurrentStep(1);
  }, []);

  const skip = useCallback(() => {
    const next = Math.min(currentStep + 1, TOTAL_STEPS - 1);
    setCurrentStep(next);
    setErrors({});
    setVisitedSteps((prev) => {
      if (prev.has(next)) return prev;
      return new Set([...prev, next]);
    });
  }, [currentStep]);

  const goBack = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
    setErrors({});
  }, []);

  const goToStep = useCallback((step: number) => {
    if (visitedSteps.has(step)) {
      setCurrentStep(step);
      setErrors({});
    }
  }, [visitedSteps]);

  const validateAll = useCallback((): boolean => {
    for (let i = 0; i < TOTAL_STEPS; i++) {
      const errs = validateStep(i, form, isPF);
      if (Object.keys(errs).length > 0) {
        setCurrentStep(i);
        setErrors(errs);
        const firstField = Object.keys(errs)[0];
        setTimeout(() => document.getElementById(firstField)?.focus(), 100);
        return false;
      }
    }
    return true;
  }, [form, isPF]);

  return useMemo(() => ({
    form,
    errors,
    set,
    currentStep,
    visitedSteps,
    isPF,
    isEditing,
    savedClienteId,
    validateCurrentStep,
    goNext,
    goBack,
    goToStep,
    skip,
    validateAll,
    handleCreated,
  }), [form, errors, set, currentStep, visitedSteps, isPF, isEditing, savedClienteId, validateCurrentStep, goNext, goBack, goToStep, skip, validateAll, handleCreated]);
}
